import React, { useState, useRef, useEffect } from "react";
import { X, Mail, KeyRound, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";

const TC_API = "https://api.calvant.com/trust-service"

/**
 * ReclaimAccessModal
 *
 * Props:
 *   organization  {string}  — org identifier for the trust centre
 *   shareToken    {string}  — active share token (passed through to API)
 *   onClose       {fn}      — close the modal
 *   onSuccess     {fn(sessionToken)} — called when OTP verified; parent stores the token
 */
export default function ReclaimAccessModal({ organization, shareToken, onClose, onSuccess }) {
  const [step, setStep]         = useState("email");   // "email" | "otp" | "success"
  const [email, setEmail]       = useState("");
  const [otp, setOtp]           = useState(["", "", "", "", "", ""]);
  const [loading, setLoading]   = useState(false);
  const [errMsg, setErrMsg]     = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  const otpRefs = useRef([]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // ── Step 1: Send OTP ─────────────────────────────────────────────────────
  const handleSendOtp = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/\S+@\S+\.\S+/.test(trimmed)) {
      setErrMsg("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setErrMsg("");
    try {
      const res = await fetch(`${TC_API}/api/access-requests/public/reclaim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, organization }),
      });
      // Always 200 to prevent email enumeration — backend silently ignores unknown emails
      if (!res.ok) throw new Error(await res.text());
      setStep("otp");
      setResendTimer(60);
      // Focus first OTP box
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    } catch (e) {
      setErrMsg(e.message || "Failed to send code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input handling ───────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    const char = value.replace(/\D/g, "").slice(-1);
    const next = [...otp];
    next[index] = char;
    setOtp(next);
    setErrMsg("");
    if (char && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
      e.preventDefault();
    }
  };

  // ── Step 2: Verify OTP ───────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    const code = otp.join("");
    if (code.length < 6) { setErrMsg("Please enter the 6-digit code."); return; }
    setLoading(true);
    setErrMsg("");
    try {
      const res = await fetch(`${TC_API}/api/access-requests/public/reclaim/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), organization, otp: code }),
      });
      if (res.status === 401) { setErrMsg("Invalid or expired code. Please try again."); setLoading(false); return; }
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json(); // { sessionToken, expiresAt }
      setStep("success");
      onSuccess(data.sessionToken);
    } catch (e) {
      setErrMsg(e.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setOtp(["", "", "", "", "", ""]);
    setErrMsg("");
    await handleSendOtp();
  };

  // ── Shared styles ────────────────────────────────────────────────────────
  const btnPrimary = (disabled) => ({
    width: "100%", padding: "11px 0", borderRadius: 8, border: "none",
    background: disabled ? "#93c5fd" : "#1d4ed8",
    color: "white", fontWeight: 700, fontSize: 14,
    cursor: disabled ? "default" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    transition: "background 0.15s",
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "white", borderRadius: 14, width: "100%", maxWidth: 420,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)", overflow: "hidden",
        }}
      >

        {/* ── Header ────────────────────────────────────────────────────── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "18px 24px", borderBottom: "1px solid #e5e7eb",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {step === "otp" && (
              <button
                onClick={() => { setStep("email"); setErrMsg(""); setOtp(["","","","","",""]); }}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  color: "#6b7280", padding: 4, borderRadius: 6,
                  display: "flex", alignItems: "center",
                }}
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#111827" }}>
              Reclaim access
            </h2>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer",
            color: "#6b7280", padding: 4, borderRadius: 6,
            display: "flex", alignItems: "center",
          }}>
            <X size={18} />
          </button>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div style={{ padding: "28px 24px 24px" }}>

          {/* ── SUCCESS ── */}
          {step === "success" && (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 14, textAlign: "center", padding: "12px 0 8px",
            }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%",
                background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <CheckCircle size={30} color="#22c55e" />
              </div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>
                Access restored!
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>
                You now have access to all approved documents and certifications.
              </p>
              <button
                onClick={onClose}
                style={{
                  marginTop: 8, padding: "10px 32px", borderRadius: 8, border: "none",
                  background: "#1d4ed8", color: "white", fontWeight: 700,
                  fontSize: 14, cursor: "pointer",
                }}
              >
                View documents
              </button>
            </div>
          )}

          {/* ── EMAIL STEP ── */}
          {step === "email" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: "#eff6ff",
                  display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
                }}>
                  <Mail size={22} color="#1d4ed8" />
                </div>
                <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
                  Enter the email address you used when requesting access. We'll send you a
                  verification code to restore your session.
                </p>
              </div>

              {errMsg && (
                <div style={{
                  display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8,
                  background: "#fef2f2", border: "1px solid #fecaca",
                }}>
                  <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: "#b91c1c" }}>{errMsg}</span>
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrMsg(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleSendOtp()}
                  placeholder="jane@company.com"
                  autoFocus
                  style={{
                    borderRadius: 8, border: "1.5px solid #d1d5db",
                    padding: "10px 14px", fontSize: 14, color: "#111827",
                    outline: "none", width: "100%", boxSizing: "border-box",
                  }}
                  onFocus={(e) => { e.target.style.borderColor = "#1d4ed8"; }}
                  onBlur={(e) => { e.target.style.borderColor = "#d1d5db"; }}
                />
              </div>

              <button
                onClick={handleSendOtp}
                disabled={loading}
                style={btnPrimary(loading)}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)",
                      borderTop: "2px solid white", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite", display: "inline-block",
                    }} />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail size={15} />
                    Send verification code
                  </>
                )}
              </button>
            </div>
          )}

          {/* ── OTP STEP ── */}
          {step === "otp" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, background: "#eff6ff",
                  display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14,
                }}>
                  <KeyRound size={22} color="#1d4ed8" />
                </div>
                <p style={{ margin: 0, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>
                  We sent a 6-digit code to{" "}
                  <strong style={{ color: "#111827" }}>{email}</strong>.
                  It expires in 15 minutes.
                </p>
              </div>

              {errMsg && (
                <div style={{
                  display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8,
                  background: "#fef2f2", border: "1px solid #fecaca",
                }}>
                  <AlertCircle size={15} color="#ef4444" style={{ flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 13, color: "#b91c1c" }}>{errMsg}</span>
                </div>
              )}

              {/* 6-box OTP input */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 10 }}>
                  Verification code
                </label>
                <div style={{ display: "flex", gap: 8 }} onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      style={{
                        width: 48, height: 54, textAlign: "center",
                        fontSize: 22, fontWeight: 700, color: "#111827",
                        border: digit
                          ? "2px solid #1d4ed8"
                          : "1.5px solid #d1d5db",
                        borderRadius: 10, outline: "none",
                        background: digit ? "#eff6ff" : "white",
                        transition: "all 0.1s",
                        caretColor: "#1d4ed8",
                      }}
                      onFocus={(e) => { e.target.style.borderColor = "#1d4ed8"; e.target.style.boxShadow = "0 0 0 3px rgba(29,78,216,0.12)"; }}
                      onBlur={(e) => { e.target.style.boxShadow = "none"; if (!digit) e.target.style.borderColor = "#d1d5db"; }}
                    />
                  ))}
                </div>
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join("").length < 6}
                style={btnPrimary(loading || otp.join("").length < 6)}
              >
                {loading ? (
                  <>
                    <span style={{
                      width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)",
                      borderTop: "2px solid white", borderRadius: "50%",
                      animation: "spin 0.8s linear infinite", display: "inline-block",
                    }} />
                    Verifying…
                  </>
                ) : (
                  <>
                    <KeyRound size={15} />
                    Verify & restore access
                  </>
                )}
              </button>

              <div style={{ textAlign: "center" }}>
                <span style={{ fontSize: 13, color: "#6b7280" }}>Didn't receive it? </span>
                <button
                  onClick={handleResend}
                  disabled={resendTimer > 0}
                  style={{
                    background: "none", border: "none", padding: 0,
                    fontSize: 13, fontWeight: 600,
                    color: resendTimer > 0 ? "#9ca3af" : "#1d4ed8",
                    cursor: resendTimer > 0 ? "default" : "pointer",
                  }}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend code"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}